/**
 * Implements infinite scrolling functionality for a web component.
 *  This component loads additional content automatically as the user scrolls to the bottom of the page.
 *  It requires an element with class `.js-pagination__next-button` for fetching the next set of items.
 *
 * Usage:
 *  {%- paginate blog.articles by section.settings.items_per_page -%}
 *    <infinite-scroll data-infinite-scroll-enabled="{% if settings.pagination_style == 'infinite' %}true{% else %}false{% endif %}">
 *      <div class="js-pagination__item">One</div>
 *      <div class="js-pagination__item">Two</div>
 *      <div class="js-pagination__item">Three</div>
 *
 *      {%- if paginate.pages > 1 -%}
 *        {% render 'pagination', paginate: paginate %}
 *      {%- endif -%}
 *    </infinite-scroll>
 *  {%- endpaginate -%}
 */
if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    // Cleans up resources when the element is removed from the DOM
    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button');

      const infiniteOnly = this.dataset.infiniteOnly === 'true';
      const hasNextUrl = infiniteOnly && this.paginationContainer?.dataset?.nextUrl;
      const hasNextButton = this.nextButton?.hasAttribute('href');

      if (!this.paginationContainer) return;
      if (!infiniteOnly && !hasNextButton) return;
      if (infiniteOnly && !hasNextUrl && !hasNextButton) return;

      this.bindListeners();
    }

    destroy() {
      clearInterval(this.nextButtonTimer);
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.intersectionObserver?.disconnect();
      this.animationObserver?.disconnect();
    }

    getNextUrl() {
      if (this.dataset.infiniteOnly === 'true' && this.paginationContainer?.dataset?.nextUrl) {
        return this.paginationContainer.dataset.nextUrl;
      }
      return this.nextButton?.getAttribute?.('href') || '';
    }

    onNextButtonClick(event) {
      event.preventDefault();
      this.fetchNextItems();
    }

    bindListeners() {
      if (this.nextButton) {
        this.clickHandler = this.onNextButtonClick.bind(this);
        this.nextButton.addEventListener('click', this.clickHandler);
      }

      if (this.dataset.infiniteScrollEnabled === 'true') {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.fetchNextItems();
              this.nextButtonTimer = setInterval(() => this.fetchNextItems(), 1500);
            } else {
              clearInterval(this.nextButtonTimer);
            }
          });
        }, { rootMargin: `${window.innerHeight}px 0px` });
        this.intersectionObserver.observe(this.paginationContainer);
      }
    }

    /**
     * PERFORMANCE: één IntersectionObserver voor de hele levensduur van het component.
     * De oude code riep initializeScrollAnimationTrigger() zonder argument aan, waardoor er
     * bij ELKE klik een nieuwe observer werd gemaakt die de HELE pagina opnieuw observeerde,
     * zonder de vorige op te ruimen. Na 5x "Toon meer" liepen er 5 observers over
     * 60 + 120 + 180 + 240 + 300 elementen. Dat is de hoofdoorzaak van het traag worden.
     */
    getAnimationObserver() {
      if (!this.animationObserver) {
        this.animationObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry, index) => {
              const target = entry.target;
              if (entry.isIntersecting) {
                if (target.classList.contains('scroll-trigger--offscreen')) {
                  target.classList.remove('scroll-trigger--offscreen');
                  if (target.hasAttribute('data-cascade')) {
                    target.style.setProperty('--animation-order', index);
                  }
                }
                observer.unobserve(target);
              } else {
                target.classList.add('scroll-trigger--offscreen');
                target.classList.remove('scroll-trigger--cancel');
              }
            });
          },
          { rootMargin: '0px 0px -70px 0px' }
        );
      }
      return this.animationObserver;
    }

    // Observeert uitsluitend de NIEUW toegevoegde kaarten, niet de hele pagina.
    observeNewAnimations(elements) {
      if (!elements.length) return;
      const observer = this.getAnimationObserver();
      elements.forEach((el) => {
        if (el.classList?.contains('scroll-trigger')) observer.observe(el);
        el.querySelectorAll?.('.scroll-trigger').forEach((child) => observer.observe(child));
      });
    }

    /**
     * PERFORMANCE: elke productkaart bevat <link rel="stylesheet" media="print" onload="this.media='all'">
     * (component-swatch.css, component-rating.css, component-volume-pricing.css).
     * Bij elke klik werden die opnieuw in de DOM gezet. Elke media-flip dwingt een volledige
     * style recalculation af over een steeds grotere DOM.
     * Hier: nieuwe stylesheets één keer naar <head>, duplicaten weggooien.
     */
    dedupeStylesheets(elements) {
      if (!this.knownStylesheets) {
        this.knownStylesheets = new Set(
          Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((link) => link.href)
        );
      }

      elements.forEach((el) => {
        el.querySelectorAll?.('link[rel="stylesheet"]').forEach((link) => {
          const raw = link.getAttribute('href') || '';
          let href = raw;
          try {
            href = new URL(raw, window.location.origin).href;
          } catch (e) {
            /* laat href staan zoals het is */
          }

          if (this.knownStylesheets.has(href)) {
            link.remove();
            return;
          }

          this.knownStylesheets.add(href);
          const hoisted = document.createElement('link');
          hoisted.rel = 'stylesheet';
          hoisted.href = href;
          document.head.appendChild(hoisted);
          link.remove();
        });
      });
    }

    // Fetches the next set of items and updates the DOM.
    // Uses the same approach as the product finder block: fetch with ?sections=sectionId
    // to get JSON with section HTML (Shopify Section Rendering API).
    async fetchNextItems() {
      if (this.fetchInProgress) return;
      const nextUrl = this.getNextUrl();
      if (!nextUrl) return;

      this.fetchInProgress = true;
      if (this.nextButton) {
        if (!this.nextButton.dataset.originalText) {
          this.nextButton.dataset.originalText = this.nextButton.textContent || '';
        }
        this.nextButton.setAttribute('aria-disabled', 'true');
        if (window.strings?.general?.pagination?.loadingText) {
          this.nextButton.textContent = window.strings.general.pagination.loadingText;
        }
      }

      try {
        const gridEl = this.querySelector('#product-grid');
        const sectionId = gridEl?.dataset?.id;
        let html = null;
        let container = null;

        if (sectionId) {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('sections', sectionId);
          const response = await fetch(url.toString());
          if (!response.ok) throw new Error('Fetch failed: ' + response.statusText);
          const data = await response.json();
          html = data[sectionId] || null;
        }

        if (!html) {
          const response = await fetch(nextUrl);
          if (!response.ok) throw new Error('Fetch failed: ' + response.statusText);
          html = await response.text();
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        // Product grid lives inside #ProductGridContainer, but pagination controls (.js-pagination)
        // can live outside of it. Keep both scopes so we don't prematurely remove pagination.
        const productGridContainer = doc.getElementById('ProductGridContainer') || doc.body;
        container = doc.body;

        const ourGridUl = this.querySelector('#product-grid');
        const ourListUl = this.querySelector('.product-grid-list');
        const parsedGridUl = productGridContainer.querySelector('#product-grid');
        const parsedListUl = productGridContainer.querySelector('.product-grid-list');

        /**
         * PERFORMANCE: hiervoor werd elk item met .outerHTML naar een string omgezet en
         * daarna met insertAdjacentHTML opnieuw geparsed. Dat is een dubbele
         * serialiseer/parse-ronde per klik. We verplaatsen nu de al geparsede nodes direct.
         */
        let gridItems = [];
        let listItems = [];
        if (parsedGridUl && parsedListUl) {
          gridItems = Array.from(parsedGridUl.querySelectorAll('.js-pagination__item'));
          listItems = Array.from(parsedListUl.querySelectorAll('.js-pagination__item'));
        } else {
          productGridContainer.querySelectorAll('.js-pagination__item').forEach((item) => {
            if (item.querySelector('.card-list')) listItems.push(item);
            else gridItems.push(item);
          });
        }

        const appended = [];

        if (ourGridUl && gridItems.length) {
          this.dedupeStylesheets(gridItems);
          ourGridUl.append(...gridItems);
          appended.push(...gridItems);
        }
        if (ourListUl && listItems.length) {
          this.dedupeStylesheets(listItems);
          ourListUl.append(...listItems);
          appended.push(...listItems);
        }

        this.paginationItems = this.querySelectorAll('.js-pagination__item');

        // Animaties: alleen de nieuwe kaarten, via één hergebruikte observer.
        this.observeNewAnimations(appended);

        setTimeout(() => {
          if (typeof window.renderSelects === 'function') window.renderSelects();
          if (typeof window.loadDesktopOnlyTemplates === 'function') window.loadDesktopOnlyTemplates();

          // Stickers alleen opnieuw opbouwen als er daadwerkelijk stickers actief zijn.
          // stickersReinit() sloopt en herbouwt ALLE stickers op de pagina; dat is
          // kwadratisch werk als het onnodig bij elke klik draait.
          if (document.querySelector('.sticker-definition')) {
            window.stickersReinit?.();
          }

          // Swatch-carrousel: alleen initialiseren op de nieuwe kaarten.
          if (typeof window.vrooomyCarouselInit === 'function') {
            appended.forEach((el) => window.vrooomyCarouselInit(el));
          }

          // Voor andere scripts die op nieuwe items willen reageren.
          this.dispatchEvent(
            new CustomEvent('infinite-scroll:appended', {
              bubbles: true,
              detail: { items: appended },
            })
          );
        }, 300);

        this.updatePaginationControls(doc, container);
      } catch (error) {
        console.error('Infinite scroll: failed to load more items', error);
      } finally {
        this.fetchInProgress = false;
        if (this.nextButton) {
          this.nextButton.removeAttribute('aria-disabled');
          this.nextButton.textContent = this.nextButton.dataset.originalText || '';
        }
      }
    }

    updatePaginationControls(doc, container) {
      const scope = container || doc.body;
      // When fetching via Section Rendering API, the returned HTML may not include the full page
      // structure, and pagination controls might not be inside the product grid container.
      const nextPagination = scope.querySelector('.js-pagination') || doc.querySelector('.js-pagination');

      if (this.dataset.infiniteOnly === 'true') {
        // Prefer sentinel mode data-next-url, but fall back to the next button href
        // (some sections render the button-style pagination while still using infiniteOnly mode).
        const nextUrl =
          nextPagination?.getAttribute?.('data-next-url') ||
          nextPagination?.querySelector?.('.js-pagination__next-button[href]')?.getAttribute?.('href') ||
          '';
        this.paginationContainer.dataset.nextUrl = nextUrl;
        if (!nextUrl && !nextPagination?.querySelector?.('.js-pagination__next-button[href]')) {
          this.cleanupAfterLastPage();
        }
        return;
      }

      if (nextPagination && nextPagination.querySelector('.js-pagination__next-button[href]')) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this.refreshNextButton();
      } else {
        this.cleanupAfterLastPage();
      }
    }

    refreshNextButton() {
      if (!this.nextButton) return;
      this.nextButton.removeEventListener('click', this.clickHandler);
      this.nextButton = this.querySelector('.js-pagination__next-button');
      if (this.nextButton) this.nextButton.addEventListener('click', this.clickHandler);
    }

    // Removes pagination and related elements when no more pages are available
    cleanupAfterLastPage() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      clearInterval(this.nextButtonTimer);
      this.paginationContainer.remove();
      document.querySelectorAll('.js-when-paginated-only').forEach((elem) => elem.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}